import * as React from "react";
import styles from "./FaultView.module.scss";
import api from "../../axiosConfig";
import FaultList from "../../components/FaultList/FaultList";
import Search from "../../components/Search/Search";
import Category from "../../components/Category/Category";
import Fault from "../../components/Fault/Fault";
import { RouteComponentProps } from "react-router";
import { Info } from "../../components/Info/Info";
import _ from "lodash";
import {
  domainComparator,
  createPdApiSearchUrl,
  createFvSearchUrl,
  parseSearchTermsFromUrl,
  GetDomainName,
  GetElectrificationName
} from "../../utils/utils";
import queryString from "query-string";
import i18n from "../../i18n";
import { AxiosResponse } from "axios";
import TagManager from "react-gtm-module";
/**
 * FaultView contains the main logic to handle search queries to db and showing results/ single fault
 *
 */

interface OwnProps {
  languageSelectionOpen: boolean;
  setLanguageSelectionOpen: (value: boolean) => void;
  language?: string;
  setLanguage?: (lang: string) => any;
  clearItemSelectionTrigger?: boolean;
  setClearItemSelectionTrigger?: (doClear: boolean) => void;
  isFeedbackOpen: boolean;
  setFeedbackOpen: (value: boolean) => void;
}

interface FaultViewState {
  searchTerms: SearchTerms;
  results: Api.AnyFault[];
  domainOptions: Api.Domain[];
  faultPriorities: Api.FaultPriority[];
  faultDomains: Api.Domain[];
  electrifications: Api.Electrification[];
  selectedFault: Api.AnyFault | null;
  selectedCategory: Api.AnyFault | null;
  errorMessage: string | undefined;
  fetchingAuxData: boolean;
  fetchingFaultData: boolean;
  prevdDomainId: string | undefined;
  prevInElectrification: number | undefined;
  openFaultDetailsFlag: boolean;
}

class FaultView extends React.Component<
  RouteComponentProps<SearchTerms> & OwnProps,
  FaultViewState
> {
  constructor(props: RouteComponentProps<SearchTerms> & OwnProps) {
    super(props);
    const parametersFromUrl = this.props.match.params;
    const searchTermsFromUrl = parseSearchTermsFromUrl(
      queryString.parse(this.props.location.search)
    );

    // Initial state
    this.state = {
      searchTerms: {
        ...searchTermsFromUrl,
        faultCode: parametersFromUrl.faultCode || "",
        domain: parametersFromUrl.domain || "",
        electrification: searchTermsFromUrl.electrification || "-1",
        domainFilterInRequest: searchTermsFromUrl.domainFilterInRequest || "-1",
        searchCode: searchTermsFromUrl.searchCode || "",
        lang: searchTermsFromUrl.lang
      },
      results: [],
      domainOptions: [],
      faultPriorities: [],
      faultDomains: [],
      electrifications: [],
      selectedFault: null,
      selectedCategory: null,
      errorMessage: undefined,
      fetchingAuxData: false,
      fetchingFaultData: false,
      prevdDomainId: undefined,
      prevInElectrification: undefined,
      openFaultDetailsFlag: false
    };

    this.onLanguageChanged = this.onLanguageChanged.bind(this);
    this.handleLanguageChange = this.handleLanguageChange.bind(this);
    this.updateSearchSelections = this.updateSearchSelections.bind(this);
    this.handleFaultItemClick = this.handleFaultItemClick.bind(this);
  }
  ////////////////////////////////////////
  // Component lifecycle methods
  ////////////////////////////////////////
  async componentDidMount(): Promise<void> {
    i18n.on("languageChanged", this.onLanguageChanged);

    const parametersFromUrl = this.props.match.params;
    const searchTermsFromUrl = parseSearchTermsFromUrl(
      queryString.parse(this.props.location.search)
    );

    if (searchTermsFromUrl.lang) {
      this.props.setLanguage && this.props.setLanguage(searchTermsFromUrl.lang);
    }

    this.setState(prevState => ({
      ...prevState,
      searchTerms: {
        ...searchTermsFromUrl,
        faultCode: parametersFromUrl.faultCode || "",
        domain: parametersFromUrl.domain || "-1",
        electrification: searchTermsFromUrl.electrification || "-1",
        domainFilterInRequest: searchTermsFromUrl.domainFilterInRequest || "-1",
        searchCode: searchTermsFromUrl.searchCode || "",
        lang: searchTermsFromUrl.lang
      },
      fetchingAuxData: true
    }));

    // Get all auxilary data
    const domains = await api.get("domains").catch(e => {
      this.setState(prevState => ({
        ...prevState,
        errorMessage: "Error fetching domains: " + e,
        fetchingAuxData: false
      }));
    });

    const electrifications = await api.get("electrifications").catch(e => {
      this.setState(prevState => ({
        ...prevState,
        errorMessage: "Error fetching electrifications: " + e,
        fetchingAuxData: false
      }));
    });

    const faultPriorities = await api.get("faultPriorities").catch(e => {
      this.setState(prevState => ({
        ...prevState,
        errorMessage: "Error fetching faultPriorities: " + e,
        fetchingAuxData: false
      }));
    });

    // initialize the state
    this.setState(prevState => ({
      ...prevState,
      faultDomains: (domains as AxiosResponse)?.data,
      electrifications: (electrifications as AxiosResponse)?.data,
      faultPriorities: (faultPriorities as AxiosResponse)?.data,
      errorMessage: undefined,
      fetchingAuxData: false
    }));
  }

  async componentDidUpdate(
    prevProps: RouteComponentProps<SearchTerms> & OwnProps,
    prevState: FaultViewState
  ): Promise<void> {
    //--------------------------------------------------------
    // SANITY CHECK: do nothing until auxilary data is fetched
    if (this.state.faultDomains.length == 0 || this.state.faultPriorities.length == 0) {
      return;
    }

    //--------------------------------------------------------
    // Handle Kone icon press (in mobile)
    if (this.props.clearItemSelectionTrigger) {
      if (!!this.state.selectedFault) {
        // go to: fault details -> category results view (or main results view)
        const urlInElectrification = !this.state.selectedCategory
          ? undefined // clear this, if category not selected
          : this.state.searchTerms.inElectrification;
        this.setState(prevState => ({
          ...prevState,
          selectedFault: null,
          searchTerms: {
            ...prevState.searchTerms,
            faultCode: "",
            domain: "-1",
            inElectrification: urlInElectrification
          }
        }));
      } else if (!!this.state.selectedCategory) {
        // go to: category results view -> main results view
        this.setState(prevState => ({
          ...prevState,
          selectedCategory: null,
          searchTerms: {
            ...prevState.searchTerms,
            inCategoryDomain: undefined,
            inElectrification: undefined
          }
        }));
      } else {
        // go to: main results view -> info view
        this.setState(prevState => ({
          ...prevState,
          selectedCategory: null,
          searchTerms: { ...prevState.searchTerms, searchCode: undefined }
        }));
      }
      this.props.setClearItemSelectionTrigger && this.props.setClearItemSelectionTrigger(false);
      return;
    }

    //--------------------------------------------------------
    // Handle cases when url changes
    if (
      this.props.match.url !== prevProps.match.url ||
      this.props.location.search !== prevProps.location.search
    ) {
      const parametersFromUrl = this.props.match.params;
      const searchTermsFromUrl = parseSearchTermsFromUrl(
        queryString.parse(this.props.location.search)
      );

      const newSearchTerms = {
        ...this.state.searchTerms,
        faultCode: parametersFromUrl.faultCode || "",
        domain: parametersFromUrl.domain || "-1",
        inElectrification: searchTermsFromUrl.inElectrification,
        inCategoryDomain: searchTermsFromUrl.inCategoryDomain,
        electrification: searchTermsFromUrl.electrification || "-1",
        domainFilterInRequest: searchTermsFromUrl.domainFilterInRequest || "-1",
        searchCode: searchTermsFromUrl.searchCode || "",
        lang: searchTermsFromUrl.lang
      } as SearchTerms;
      const prevSearchCode =
        this.state.searchTerms.searchCode !== newSearchTerms.searchCode
          ? this.state.searchTerms.searchCode
          : this.state.searchTerms.prevSearchCode;
      const prevCode =
        this.state.searchTerms.faultCode !== newSearchTerms.faultCode
          ? this.state.searchTerms.faultCode
          : this.state.searchTerms.prevFaultCode;
      this.setState(prevState => ({
        ...prevState,
        searchTerms: { ...newSearchTerms, prevCode: prevCode, prevSearchCode: prevSearchCode }
      }));
      return;
    }

    //--------------------------------------------------------
    // Handle cases when search terms change
    if (
      !_.isEqual(prevState.searchTerms, this.state.searchTerms) ||
      prevState.fetchingAuxData !== this.state.fetchingAuxData
    ) {
      this.historyPush(createFvSearchUrl(this.state.searchTerms));

      if (!this.state.searchTerms.searchCode) {
        this.setState(prevState => ({
          ...prevState,
          results: [],
          selectedFault: null,
          selectedCategory: null,
          domainOptions: []
        }));
      } else {
        // if code or domain filter has been changed, request new list
        if (
          (!!this.state.searchTerms.searchCode &&
            this.state.searchTerms.searchCode !== this.state.searchTerms.prevSearchCode) ||
          this.state.searchTerms.electrification !== this.state.searchTerms.prevElectrification ||
          this.state.searchTerms.lang !== this.state.searchTerms.prevLang
        ) {
          const searchUrl = createPdApiSearchUrl(
            this.state.searchTerms.searchCode,
            this.state.searchTerms.lang
          );
          this.setState(prevState => ({
            ...prevState,
            fetchingFaultData: true,
            openFaultDetailsFlag: false
          }));
          const res: any = await api.get(searchUrl).catch(e => {
            this.setState(prevState => ({
              ...prevState,
              errorMessage: "Error fetching search results: " + e,
              fetchingFaultData: false
            }));
          });

          this.setState(prevState => ({
            ...prevState,
            fetchingFaultData: false
          }));
          let results = res ? res.data : [];

          if (results.length === 0) {
            const notFoundDomainName = GetDomainName(
              this.state.searchTerms.domainFilterInRequest,
              this.state.faultDomains
            );
            const notFoundElectrificationName = GetElectrificationName(
              this.state.searchTerms.electrification,
              this.state.electrifications
            );
            TagManager.dataLayer({
              dataLayer: {
                event: "no_code_found",
                code: this.state.searchTerms.searchCode,
                domain: notFoundDomainName,
                electrification: notFoundElectrificationName
              }
            });
          } else {
            const domainName = GetDomainName(
              this.state.searchTerms.domainFilterInRequest,
              this.state.faultDomains
            );
            const electrificationName = GetElectrificationName(
              this.state.searchTerms.electrification,
              this.state.electrifications
            );

            TagManager.dataLayer({
              dataLayer: {
                event: "list_of_codes_found",
                code: this.state.searchTerms.searchCode,
                domain: domainName,
                electrification: electrificationName
              }
            });
          }

          // @ts-ignore
          if (this.state.searchTerms && this.state.searchTerms.electrification >= 0) {
            const domainIds = this.getElectrificationsDomains(
              // @ts-ignore
              this.state.searchTerms.electrification
            );
            results = results.filter((fault: Api.AnyFault) => {
              return domainIds.includes(fault.domain.id);
            });
          }

          // set domain dropdown options
          const domainOptionIds: number[] = _.uniq(
            results?.map((fault: Api.AnyFault) => fault.domain.id)
          );
          const domainOptions: Api.Domain[] = this.state.faultDomains.filter((domain: Api.Domain) =>
            domainOptionIds.some(id => id === domain.id)
          );
          const domainOptionsSorted: Api.Domain[] = domainOptions.sort(
            domainComparator(domainOptions)
          );

          const domainFilterInRequest = domainOptionsSorted.some(
            opt => opt.id.toString() === this.state.searchTerms.domainFilterInRequest
          )
            ? this.state.searchTerms.domainFilterInRequest
            : "-1";

          const domainName = GetDomainName(
            this.state.searchTerms.domainFilterInRequest,
            this.state.faultDomains
          );

          const electrificationName = GetElectrificationName(
            this.state.searchTerms.electrification,
            this.state.electrifications
          );

          // Set GA tag
          TagManager.dataLayer({
            dataLayer: {
              event: "search",
              search_term: this.state.searchTerms.searchCode,
              code: this.state.searchTerms.searchCode,
              domain: domainName,
              electrification: electrificationName
            }
          });

          // store results, domainOptions and previous search terms to state
          this.setState(prevState => ({
            ...prevState,
            searchTerms: {
              ...this.state.searchTerms,
              domainFilterInRequest: domainFilterInRequest,
              prevSearchCode: this.state.searchTerms.searchCode,
              prevDomainFilterInRequest: this.state.searchTerms.domainFilterInRequest,
              prevElectrification: this.state.searchTerms.electrification,
              prevLang: this.state.searchTerms.lang
            } as SearchTerms,
            domainOptions: domainOptionsSorted || ([] as Api.Domain[]),
            results: results ?? [],
            errorMessage: undefined
          }));
        }

        this.selectFault(
          this.state.searchTerms.faultCode || "",
          +(this.state.searchTerms.domain || -1),
          this.state.searchTerms.inElectrification,
          this.state.searchTerms.inCategoryDomain,
          this.state.searchTerms.searchCode
        );
      }
    }
  }

  componentWillUnmount(): void {
    i18n.off("languageChanged", this.onLanguageChanged);
  }

  ////////////////////////////////////////
  // UI handlers
  ////////////////////////////////////////
  onLanguageChanged(lng: string): void {
    this.handleLanguageChange({ ...this.state.searchTerms, lang: lng });
  }

  handleFaultItemClick = (
    resultType: number,
    code: string,
    domainId: string,
    inElectrification?: number,
    inCategoryDomain?: number
  ): void => {
    // @ts-ignore
    const selectedFaultCode = this.state.selectedFault?.code;
    // @ts-ignore
    const selectedFaultDomainId = this.state.selectedFault?.domain.id;
    if (resultType === 0) {
      // clicked fault item
      if (
        +code === selectedFaultCode &&
        +domainId === selectedFaultDomainId &&
        inElectrification == this.state.selectedFault?.inElectrification &&
        inCategoryDomain == this.state.selectedFault?.inCategoryDomain
      ) {
        this.setState(prevState => ({
          ...prevState,
          selectedFault: null,
          selectedCategory: null
        }));

        this.historyPush(
          createFvSearchUrl({ ...this.state.searchTerms, faultCode: code, domain: undefined })
        );
      } else {
        const selectedFault = this.state.results.find(
          (fault: Api.AnyFault) => fault.domain.id === +domainId && fault.code.toString() === code
        );
        if (selectedFault) {
          if (
            domainId !== this.state.prevdDomainId ||
            inElectrification !== this.state.prevInElectrification
          ) {
            this.setState(prevState => ({
              ...prevState,
              prevdDomainId: domainId,
              prevInElectrification: inElectrification,
              openFaultDetailsFlag: true
            }));
          } else {
            this.setState(prevState => ({
              ...prevState,
              openFaultDetailsFlag: !prevState.openFaultDetailsFlag
            }));
          }

          const domainName = GetDomainName(
            selectedFault?.domain.id.toString(),
            this.state.faultDomains
          );
          const electrificationName = GetElectrificationName(
            inElectrification?.toString(),
            this.state.electrifications
          );
          let name_exists = selectedFault?.name !== null && selectedFault?.name !== "";
          let reason_exists = selectedFault?.reason !== null && selectedFault?.reason !== "";
          let action_exists = selectedFault?.action !== null && selectedFault?.action !== "";

          if (selectedFault?.localizationNotFound) {
            name_exists = false;
            reason_exists = false;
            action_exists = false;
          }

          TagManager.dataLayer({
            dataLayer: {
              event: "fault_code_details",
              electrification: electrificationName,
              domain: domainName,
              fault_code: selectedFault?.code,
              language: this.state.searchTerms.lang,
              name_exists: name_exists,
              reason_exists: reason_exists,
              action_exists: action_exists
            }
          });
        }

        this.selectFault(code, +domainId, inElectrification, inCategoryDomain);
        this.historyPush(
          createFvSearchUrl({
            ...this.state.searchTerms,
            faultCode: code,
            domain: domainId,
            inElectrification: inElectrification,
            inCategoryDomain: inCategoryDomain
          })
        );
      }
    } else if (resultType === 1) {
      // clicked category item in mobile view
      this.selectCategory(code, +domainId, inElectrification, inCategoryDomain);
      this.historyPush(
        createFvSearchUrl({
          ...this.state.searchTerms,
          inElectrification: inElectrification,
          inCategoryDomain: inCategoryDomain
        })
      );
    }
  };

  ////////////////////////////////////////
  //Helper methods
  ////////////////////////////////////////
  handleLanguageChange(searchTerms: SearchTerms): void {
    this.setState(prevState => ({
      ...prevState,
      results: [],
      selectedCategory: null,
      selectedFault: null
    }));
    const historyEntry = createFvSearchUrl(searchTerms);
    this.historyPush(historyEntry);
  }

  updateSearchSelections(searchTermsFromSearchInputs: SearchTerms): void {
    const codeChanged =
      searchTermsFromSearchInputs.searchCode !== this.state.searchTerms.searchCode;
    const newSearchTerms = {
      ...this.state.searchTerms,
      searchCode: searchTermsFromSearchInputs.searchCode,
      domain: codeChanged ? "-1" : this.state.searchTerms.domain,
      domainFilterInRequest: searchTermsFromSearchInputs.domainFilterInRequest,
      electrification: searchTermsFromSearchInputs.electrification,
      prevSearchCode: this.state.searchTerms.searchCode,
      prevDomainFilterInRequest: this.state.searchTerms.domainFilterInRequest,
      prevElectrification: this.state.searchTerms.electrification,
      prevLang: this.state.searchTerms.lang
    } as SearchTerms;
    this.setState(prevState => ({
      ...prevState,
      searchTerms: newSearchTerms,
      selectedCategory: null,
      selectedFault: null,
      openFaultDetailsFlag: false
    }));
  }

  selectFault = (
    code: string,
    domain: number,
    inElectrification?: number,
    inCategoryDomain?: number,
    searchCode?: string
  ): void => {
    const selectedFault = this.state.results.find(
      (fault: Api.AnyFault) => fault.domain.id === domain && fault.code.toString() === code
    );
    if (!!selectedFault) {
      selectedFault.inElectrification = inElectrification;
      selectedFault.inCategoryDomain = inCategoryDomain;
      // check if the fault belongs to a category, set selectedCategory
      const selectedCategory = this.state.results.find(
        (fault: Api.AnyFault) =>
          fault.resultType === 1 &&
          (selectedFault as Api.DriveFault)?.faultCategory?.id === fault.id &&
          selectedFault.domain.id === fault.domain.id &&
          fault.inElectrification == inElectrification
      );
      if (!!selectedCategory) {
        selectedCategory.inElectrification = inElectrification;
      }

      this.setState(prevState => ({
        ...prevState,
        selectedFault: selectedFault,
        selectedCategory: selectedCategory || null,
        errorMessage: undefined
      }));
    } else if (!!inCategoryDomain && !!searchCode) {
      const selectedCategory = this.state.results
        .filter(result => result.resultType == 1)
        .find(
          (fault: Api.AnyFault) =>
            fault.resultType === 1 &&
            fault.code.toString() === searchCode &&
            fault.domain.id == inCategoryDomain
        );
      this.setState(prevState => ({
        ...prevState,
        selectedFault: null,
        selectedCategory: selectedCategory || null,
        errorMessage: undefined
      }));
    } else {
      this.setState(prevState => ({
        ...prevState,
        selectedFault: null,
        selectedCategory: null,
        errorMessage: undefined
      }));
    }
  };

  selectCategory = (
    code: string,
    domain: number,
    inElectrification?: number,
    inCategoryDomain?: number
  ): void => {
    const selectedCategory = this.state.results.find(
      (fault: Api.AnyFault) =>
        fault.domain.id === domain && fault.code === +code && fault.resultType === 1
    );
    if (!!selectedCategory) {
      selectedCategory.inElectrification = inElectrification;
      selectedCategory.inCategoryDomain = inCategoryDomain;
      this.setState(prevState => ({
        ...prevState,
        selectedCategory: selectedCategory,
        errorMessage: undefined
      }));
    } else {
      this.setState(prevState => ({
        ...prevState,
        selectedCategory: null,
        errorMessage: undefined
      }));
    }
  };

  // TODO: Possible hotstop due to multiple rule violations?
  getElectrificationsDomains(electrificationEnumValue: number): number[] {
    const domainsFiltered = this.state.faultDomains.filter(domain =>
      // @ts-ignore
      domain.electrifications.find((e: any) => e.enumValue === Number(electrificationEnumValue))
    );
    // @ts-ignore
    return domainsFiltered.map(d => d.id);
  }

  historyPush(newpath: string): void {
    const currentPath = this.props.history.location.pathname + this.props.history.location.search;
    if (currentPath !== newpath) {
      this.props.history.push(newpath);
    }
  }

  render(): JSX.Element {
    const resultDivClasses = [styles.FaultViewResults];
    const searchDivClasses = [""];
    const { selectedCategory, selectedFault, faultDomains, faultPriorities } = this.state;

    let category: JSX.Element = <div></div>;
    if (selectedCategory && faultDomains.length) {
      const faultDomain: any = faultDomains.find(
        // @ts-ignore
        (domain: any) => domain.id === selectedCategory.domain.id
      );

      const childItems = this.state.results
        .filter(
          // currently only DriveFault has a category
          (item: Api.AnyFault) =>
            (item as Api.DriveFault)?.faultCategory?.id === selectedCategory.id &&
            item.domain.id == selectedCategory.domain.id
        )
        .map(driveFault => {
          return {
            ...driveFault,
            inElectrification: selectedCategory.inElectrification,
            inCategoryDomain: selectedCategory.domain.id
          };
        });

      // @ts-ignore
      category = (
        <Category
          groupItem={selectedCategory}
          childItems={childItems}
          faultPriorities={faultPriorities}
          faultDomains={faultDomains}
          onItemClick={this.handleFaultItemClick}
          selectedFault={selectedFault}
          domain={faultDomain}
          eleEnumValue={selectedCategory.inElectrification}
        />
      );
      resultDivClasses.push(styles.FaultViewHideForMobile);
      searchDivClasses.push(styles.FaultViewHideForMobile);
    }

    let fault: JSX.Element = <div></div>;
    if (selectedFault && faultPriorities.length && faultDomains.length) {
      const faultPriority = faultPriorities.find(
        // @ts-ignore
        (priority: any) => priority.id === selectedFault.priority.id
      );
      const faultDomain: any = faultDomains.find(
        // @ts-ignore
        (domain: any) => domain.id === selectedFault.domain.id
      );
      // @ts-ignore
      fault = <Fault fault={selectedFault} priority={faultPriority} domain={faultDomain} />;
      resultDivClasses.push(styles.FaultViewHideForMobile);
      searchDivClasses.push(styles.FaultViewHideForMobile);
    }

    let resultsList = <Info />;
    if (
      this.state.results.length > 0 &&
      this.state.faultPriorities.length &&
      this.state.faultDomains.length > 0 &&
      this.state.electrifications.length > 0
    ) {
      resultsList = (
        <FaultList
          selectedFault={selectedFault}
          faultDomains={this.state.faultDomains}
          electrifications={this.state.electrifications}
          faults={this.state.results}
          faultPriorities={this.state.faultPriorities}
          onItemClick={this.handleFaultItemClick}
          searchTerms={this.state.searchTerms}
        />
      );
    } else if (this.state.fetchingAuxData || this.state.fetchingFaultData) {
      resultsList = <p>...Fetching data...</p>;
    } else {
      resultsList = <p>No results found for code: {this.state.searchTerms.searchCode}</p>;
    }

    return (
      <>
        <div className={searchDivClasses.join(" ")}>
          <Search
            // options to dropdowns
            domains={this.state.faultDomains}
            domainOptions={this.state.domainOptions}
            electrifications={this.state.electrifications}
            onSearchTermsChange={this.updateSearchSelections}
            currentFaultCode={this.state.searchTerms.searchCode}
            currentDomainId={+(this.state.searchTerms.domainFilterInRequest || -1)}
            currentElectrification={+(this.state.searchTerms.electrification || -1)}
            languageSelectionOpen={this.props.languageSelectionOpen}
            setLanguageSelectionOpen={this.props.setLanguageSelectionOpen}
            isFeedbackOpen={this.props.isFeedbackOpen}
            setFeedbackOpen={this.props.setFeedbackOpen}
          />
        </div>
        {!!this.state.errorMessage && <p className={styles.error}>{this.state.errorMessage}</p>}
        {this.state.results.length === 0 && !this.state.searchTerms.searchCode && !selectedFault ? (
          <div className={styles.InfoWrapper}>
            <Info />
          </div>
        ) : (
          <div className={styles.FaultViewContent}>
            <div className={resultDivClasses.join(" ")}>{resultsList}</div>
            {selectedCategory && !selectedFault && (
              <div className={[styles.FaultViewDetails, styles.OnlyInMobile].join(" ")}>
                {category}
              </div>
            )}
            {this.state.openFaultDetailsFlag && (
              <div className={styles.FaultViewDetails}>{fault}</div>
            )}
          </div>
        )}
      </>
    );
  }
}

export default FaultView;
